import { db } from '../firebaseConfig';
import { doc, getDoc, setDoc } from 'firebase/firestore';

/**
 * Service pour lire la configuration du parcours depuis Firebase.
 * Collection: parcoursConfig, Document: main
 *
 * La config contient les règles de déblocage (modifiables sans redéploiement)
 * et les messages de Marcel.
 */
class ParcoursConfigService {
  constructor() {
    this._cache = null;
    this._cacheTimestamp = null;
    this._freeCache = null; // mode gratuit (parcoursConfig/free)
    this._freeCacheTimestamp = null;
    this._cacheDuration = 5 * 60 * 1000; // 5 minutes
  }

  /**
   * Récupère la config complète du parcours (avec cache)
   * @returns {Promise<Object|null>}
   */
  async getConfig({ anonymous = false } = {}) {
    if (anonymous) return this._getFreeConfig();
    // Retourner le cache si encore valide
    if (this._cache && this._cacheTimestamp && (Date.now() - this._cacheTimestamp < this._cacheDuration)) {
      return this._cache;
    }

    try {
      const configRef = doc(db, 'parcoursConfig', 'main');
      const configDoc = await getDoc(configRef);

      if (!configDoc.exists()) {
        console.warn('⚠️ parcoursConfig/main non trouvé — utilisation des valeurs par défaut');
        this._cache = this._getDefaultConfig();
      } else {
        this._cache = configDoc.data();
      }

      this._cacheTimestamp = Date.now();
      return this._cache;
    } catch (error) {
      console.error('❌ Erreur lecture parcoursConfig:', error);
      // En cas d'erreur, utiliser les valeurs par défaut
      return this._getDefaultConfig();
    }
  }

  /**
   * Mode gratuit (compte anonyme) : lit parcoursConfig/free (chapitre 1 seulement),
   * jamais parcoursConfig/main (refusé aux anonymes par les règles Firestore).
   */
  async _getFreeConfig() {
    if (this._freeCache && this._freeCacheTimestamp && (Date.now() - this._freeCacheTimestamp < this._cacheDuration)) {
      return this._freeCache;
    }
    try {
      const configDoc = await getDoc(doc(db, 'parcoursConfig', 'free'));
      this._freeCache = configDoc.exists() ? configDoc.data() : this._getDefaultConfig();
      this._freeCacheTimestamp = Date.now();
      return this._freeCache;
    } catch (error) {
      console.error('❌ Erreur lecture parcoursConfig/free:', error);
      return this._getDefaultConfig();
    }
  }

  /**
   * Récupère uniquement les règles de déblocage
   * @returns {Promise<Object>}
   */
  async getUnlockRules() {
    const config = await this.getConfig();
    return config?.unlockRules || {};
  }

  /**
   * Récupère les messages de Marcel
   * @returns {Promise<Object>}
   */
  async getMarcelMessages() {
    const config = await this.getConfig();
    return config?.marcelMessages || {};
  }

  /**
   * Sauvegarde la config dans Firestore (parcoursConfig/main)
   * @param {Object} config - La config complète à sauvegarder
   * @returns {Promise<boolean>} true si succès
   */
  async saveConfig(config) {
    try {
      const configRef = doc(db, 'parcoursConfig', 'main');
      await setDoc(configRef, config);
      this._cache = config;
      this._cacheTimestamp = Date.now();
      return true;
    } catch (error) {
      console.error('❌ Erreur sauvegarde parcoursConfig:', error);
      return false;
    }
  }

  /**
   * Initialise le document Firestore avec les valeurs par défaut
   * @returns {Promise<boolean>} true si succès
   */
  async initializeDefaultConfig() {
    return this.saveConfig(this._getDefaultConfig());
  }

  /**
   * Vérifie si le document existe dans Firestore
   * @returns {Promise<boolean>}
   */
  async existsInFirestore() {
    try {
      const configRef = doc(db, 'parcoursConfig', 'main');
      const configDoc = await getDoc(configRef);
      return configDoc.exists();
    } catch {
      return false;
    }
  }

  /**
   * Force le rechargement de la config (utile après modification admin)
   */
  invalidateCache() {
    this._cache = null;
    this._cacheTimestamp = null;
    this._freeCache = null;
    this._freeCacheTimestamp = null;
  }

  /**
   * Config par défaut si le document Firebase n'existe pas encore.
   * Retourne un schéma v3 minimal (chapitres linéaires vides).
   * Le fallback complet avec les steps est dans parcoursStructure.js,
   * chargé via parcoursConfigLoader.js.
   */
  _getDefaultConfig() {
    return {
      version: 2,
      chapitres: {
        1: { name: 'Chapitre 1 — Les Fondations', type: 'linear', steps: [] }
      },
      marcelMessages: []
    };
  }
}

export default new ParcoursConfigService();
